"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ChevronRight, Cpu, Layers, Microscope, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface SolutionItem {
  id: string;
  title: string;
  description: string;
  category: string;
  url: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  tags: string[];
}
const solutions: SolutionItem[] = [{
  id: "pat-core",
  title: "Process Analytical Technology",
  description: "In-line and on-line real-time monitoring for pharmaceutical and chemical synthesis, blending, and granulation.",
  category: "Process Monitoring",
  url: "/ProcessAnalyticalTechnologyPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_spectroscopy_analyzer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_spectroscopy_analyzer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_spectroscopy_analyzer.png-wvc-sizes",
  imageAlt: "Process Analytical Technology NIR spectrometer sensor installed on industrial manufacturing port",
  tags: ["Real-Time NIR", "GMP Compliant", "Direct Integration"]
}, {
  id: "blend-uniformity",
  title: "Blend Uniformity & Endpoint",
  description: "Wireless, battery-operated NIR sensors designed for real-time blender and mixer homogeneity verification.",
  category: "Formulation Control",
  url: "/BlendUniformityPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_nir.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_nir.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_nir.png-wvc-sizes",
  imageAlt: "Wireless MicroNIR spectrometer attached to rotary tumble blender for blend monitoring",
  tags: ["Non-invasive", "ATEX Certified", "Automated Stop"]
}, {
  id: "drying-endpoint",
  title: "Drying Endpoint & Moisture",
  description: "Continuous moisture and solvent monitoring in fluid bed dryers, vacuum dryers, and granulation systems.",
  category: "Batch Optimization",
  url: "/DryingEndpointPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_sensor.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_sensor.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_sensor.png-wvc-sizes",
  imageAlt: "Moisture monitoring NIR probe assembly in pharmaceutical drying process",
  tags: ["Solvent Control", "Yield Optimization", "Zero Sampling Loss"]
}];
const highlights = [{
  icon: ShieldCheck,
  title: "Regulatory Compliance",
  desc: "Full 21 CFR Part 11 and EU Annex 11 validation packages for regulated facilities."
}, {
  icon: Cpu,
  title: "Custom Chemometrics",
  desc: "Quantitative and qualitative chemometric model development with full tech transfer."
}, {
  icon: Layers,
  title: "Hardware Integration",
  desc: "Mechanical, optical, and industrial network installation across Ireland and the UK."
}];
export default function RelatedSolutions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedsolutionscta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border relative overflow-hidden">
      <div className="page-container relative z-10">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-semibold text-xs tracking-wide uppercase">
              Application Pathways
            </Badge>
            <span className="text-muted-foreground text-sm font-medium">Antech PAT Ecosystem</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Related Analytical &amp; In-Line Process Solutions
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Pair robust process instrumentation with qualified implementation support to eliminate off-line bottlenecks and safeguard product quality.
          </p>
        </div>

        {/* Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {solutions.map((item, index) => <motion.div key={item.id} data-index={index} variants={itemVariants} className="flex">
              <Card className="flex flex-col justify-between w-full h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200 overflow-hidden group">
                <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                  <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute top-3 left-3">
                    <Badge variant="default" className="bg-primary text-primary-foreground shadow-xs">
                      {item.category}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6 flex flex-col flex-1 justify-between gap-6">
                  <div className="space-y-3">
                    <h3 className="text-xl font-bold text-card-foreground group-hover:text-primary transition-colors duration-150">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 pt-2">
                      {item.tags.map(tag => <span key={tag} className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                          <CheckCircle2 className="size-3 text-primary" />
                          {tag}
                        </span>)}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <Button variant="outline" className="w-full justify-between group-hover:border-primary group-hover:text-primary" asChild>
                      <Link to={item.url}>
                        <span>Explore Solution</span>
                        <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Feature Highlights Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 p-6 md:p-8 rounded-xl bg-secondary/60 border border-border mb-16">
          {highlights.map((h, i) => {
          const Icon = h.icon;
          return <div key={h.title} data-index={i} className="flex items-start gap-3.5">
                <div className="p-2.5 rounded-lg bg-card text-primary border border-border shrink-0 shadow-2xs">
                  <Icon className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">{h.title}</h4>
                  <p className="text-xs text-muted-foreground leading-normal">{h.desc}</p>
                </div>
              </div>;
        })}
        </div>

        {/* Main CTA Banner */}
        <div className="relative rounded-2xl bg-card border border-border p-8 md:p-12 shadow-sm overflow-hidden">
          <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <Badge variant="outline" className="mb-3 border-border text-foreground font-medium">
                Technical Consultation
              </Badge>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-3">
                Need help evaluating feasibility for your process?
              </h3>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                Our application specialists in Dublin and Newcastle review sample spectra, mechanical constraints, and compliance requirements to recommend the optimal PAT setup.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto shrink-0">
              <Button size="lg" className="font-semibold shadow-xs" asChild>
                <Link to="/BookADemoPage">
                  <span>Book Instrument Demo</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="font-medium" asChild>
                <Link to="/DiscussYourApplicationPage">
                  <span>Discuss Application</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}